import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterLostWage,
  useDeleteMatterLostWage,
  useUpdateMatterLostWage,
} from "@/lib/data/react-query/matters/matter-lost-wage";
import { MatterLostWageSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type { MatterLostWage } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { calculateLostWages } from "@/lib/utils/math";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { isNil, omitBy } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditLostWageModalProps {
  title: string;
  description?: string;
  editing?: boolean;
  matterId: string;
  lostWageId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData?: Partial<MatterLostWage>;
}

const lostWageFormSchema = MatterLostWageSchema.omit({
  dateCreated: true,
  dateModified: true,
  matterId: true,
  id: true,
}).superRefine((data, ctx) => {
  const isEmployed = data.employed === true;
  if (isEmployed && !data.positionTitle?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Position title is required",
      path: ["positionTitle"],
    });
  }
});

const defaultFormValues = {
  positionTitle: "",
  positionRate: null,
  description: undefined,
  employed: true,
};

export const AddOrEditLostWageModal = ({
  title,
  description,
  editing,
  matterId,
  lostWageId,
  open,
  setOpen,
  initialData,
}: AddOrEditLostWageModalProps) => {
  const { updateSlideoverPayload } = useModals();
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasActionPermission } = useUser();
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLostWages,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLostWages,
    ACTION_PERMISSION_VALUES.delete,
  );
  const lostWageModalFormId = "add-matter-lost-wage-modal-form";

  // Fetch matter data
  const {
    data: matterData,
    isLoading: isMatterLoading,
    error: matterError,
  } = useQuery(retrieveMatterQueryOptions(matterId));

  // Get the specific lost wage if editing
  const lostWage =
    lostWageId && matterData?.data
      ? matterData.data.lostWages.find((lostWage) => lostWage.id === lostWageId)
      : undefined;
  const lostWageInitialValues = mergeInitialData(lostWage, initialData);

  const matter = matterData?.data;

  const lostWageForm = useForm<z.infer<typeof lostWageFormSchema>>({
    resolver: zodResolver(lostWageFormSchema),
    values:
      editing && lostWageInitialValues
        ? {
            ...defaultFormValues,
            ...lostWageInitialValues,
          }
        : {
            ...defaultFormValues,
          },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const createLostWage = useCreateMatterLostWage();
  const updateLostWage = useUpdateMatterLostWage();
  const deleteLostWage = useDeleteMatterLostWage();

  const positionType = lostWageForm.watch("positionType");
  const positionRate = lostWageForm.watch("positionRate");
  const timeMissed = lostWageForm.watch("timeMissed");
  const isEmployed = lostWageForm.watch("employed") === true;
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const isFormLoading = createLostWage.isPending || updateLostWage.isPending;

  const onSubmit = async (data: z.infer<typeof lostWageFormSchema>) => {
    if (!matter) return;
    if (editing && !hasUpdatePermission) return;

    let resData;
    if (editing && lostWageId && lostWage) {
      const res = await updateLostWage.mutateAsync({
        ...data,
        matterId: matterId,
        dateModified: lostWage.dateModified,
        id: lostWageId,
      });
      resData = res;
    } else {
      resData = await createLostWage.mutateAsync({
        matterId: matterId,
        ...omitBy(data, isNil), // filter out null values on creation
      });
      updateSlideoverPayload("lost-wage", {
        title: SLIDEOVER_TITLE.LOST_WAGE.UPDATE,
        description: SLIDEOVER_DESCRIPTION.LOST_WAGE.UPDATE,
        editing: true,
        lostWageId: resData.data.id,
      });
      lostWageForm.reset({
        ...defaultFormValues,
        ...omitBy(resData.data, isNil),
      });
    }
    if (closeAfterSave) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) {
      lostWageForm.reset();
    }
  }, [open, lostWageForm]);

  // Show loading state while fetching data
  if (isMatterLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  // Show error state if data fetching failed
  if (matterError || !matter) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Error loading matter data</p>
        </div>
      </div>
    );
  }

  const lostWageFieldset = (
    <fieldset
      className={cn(
        slideoverFieldsetClassName,
        "grid-cols-1 md:grid-cols-2",
        "my-0 p-0",
      )}
      disabled={editing ? !hasUpdatePermission : false}
    >
      <div className="col-span-full flex h-[62px] items-center justify-start">
        <SwitchFormField
          register={lostWageForm.register("employed")}
          label="Employed"
        />
      </div>
      {isEmployed && (
        <>
          <TextFormField
            register={lostWageForm.register("positionTitle")}
            label="Position title*"
          />
          <CurrencyFormField
            register={lostWageForm.register("positionRate")}
            label="Position rate"
          />
          <SimpleSelectFormField
            register={lostWageForm.register("positionType")}
            label="Position type"
            placeholder="Select position type"
            items={["Hourly", "Salary"]}
            clearable
          />
          <TextFormField
            register={lostWageForm.register("timeMissed")}
            label="Days missed"
          />
          <div className="col-span-full mt-4">
            {!isNil(positionRate) && !isNil(timeMissed) && positionType && (
              <Typography variant="subHeading">
                {`Approximate Total Lost Wages: `}
                <CurrencyWrapper
                  value={calculateLostWages(
                    positionType,
                    positionRate,
                    timeMissed,
                  )}
                />
              </Typography>
            )}
          </div>
        </>
      )}
      <div className="col-span-2">
        <TextAreaFormField
          register={lostWageForm.register("description")}
          label="Description"
        />
      </div>
    </fieldset>
  );

  const modalFooter = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        {editing && lostWageId && hasDeletePermission && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setIsDeleting(true);
            }}
          >
            Delete
          </Button>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <FormCancelButton
          setOpen={setOpen}
          isDirty={lostWageForm.formState.isDirty}
        />
        <FormSubmitButton
          variant="light-primary"
          loading={isFormLoading}
          disabled={editing ? !hasUpdatePermission : false}
          overrideDisabled={Boolean(initialData)}
          submitFn={() => {
            setCloseAfterSave(false);
          }}
          title="Save"
          submitFormId={lostWageModalFormId}
        />
        <FormSubmitButton
          variant="default"
          loading={isFormLoading}
          disabled={editing ? !hasUpdatePermission : false}
          overrideDisabled={Boolean(initialData)}
          submitFn={() => {
            setCloseAfterSave(true);
          }}
          title="Save and close"
          submitFormId={lostWageModalFormId}
        />
      </div>
    </div>
  );

  return (
    <>
      <Form {...lostWageForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="2xl"
          loading={isFormLoading}
          isDirty={lostWageForm.formState.isDirty}
          footer={modalFooter}
        >
          <form
            id={lostWageModalFormId}
            className="relative flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void lostWageForm.handleSubmit(onSubmit)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            {lostWageFieldset}
          </form>
        </AppModal>
      </Form>

      {editing && lostWageId && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Are you sure you want to delete this lost wage?"
          description="This action cannot be undone. This will remove the lost wage and all associated tasks and files."
          afterActionFn={() => {
            deleteLostWage.mutate({
              matterId: matterId,
              id: lostWageId,
            });
            setOpen(false);
          }}
        />
      )}

      {/* {providerContactId && (
        <EditContactModal
          activeContactId={providerContactId}
          open={isEditingContact}
          setOpen={setIsEditingContact}
          hasEditPermission={hasActionPermission(
            ACTION_PERMISSION_KEYS.contact,
            ACTION_PERMISSION_VALUES.update,
          )}
        />
      )} */}
    </>
  );
};
